import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
} from '@mui/material';
import RestoreIcon from '@mui/icons-material/Restore';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { API_BASE_URL } from '../../../config/api';
import './AssistantSettingsTheme.css';

/**
 * A small "reverse" (restore) icon that opens a dialog listing the previous
 * versions of an agent's instructions. Each version can be expanded to read the
 * full prompt, copied (in whole or just a highlighted section), or restored.
 * "Restore" loads that version back into the editor via the `onRestore`
 * callback (the parent is responsible for saving).
 *
 * Props:
 *  - agentId:     the agent UUID (required to fetch history).
 *  - channelType: optional channel name ("WEB" | "VAPI" | "REALTIME"). Omit for
 *                 the agent-level instructions history.
 *  - onRestore:   (instructions: string) => void, called when a version is picked.
 */
const InstructionHistoryButton = ({ agentId, channelType = null, onRestore }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  // Transient per-entry copy feedback, e.g. "12-all" | "12-sel" | "12-none".
  const [copiedKey, setCopiedKey] = useState('');

  const fetchHistory = async () => {
    if (!agentId) {
      setError('Save the assistant first to start tracking instruction history.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const qs = channelType ? `?channel_type=${encodeURIComponent(channelType)}` : '';
      const response = await fetch(
        `${API_BASE_URL}/api/agents/${agentId}/instructions/history${qs}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to load history (status: ${response.status})`);
      }
      const data = await response.json();
      const list = Array.isArray(data) ? data : [];
      setHistory(list);
      // Expand the most recent version by default for quick reading.
      setExpandedId(list.length > 0 ? list[0].id : null);
    } catch (err) {
      setError(err.message || 'Failed to load instruction history.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setCopiedKey('');
    fetchHistory();
  };

  const handleRestore = (entry) => {
    if (onRestore) {
      onRestore(entry.instructions || '');
    }
    setOpen(false);
  };

  const flashCopied = (key) => {
    setCopiedKey(key);
    setTimeout(() => {
      setCopiedKey((current) => (current === key ? '' : current));
    }, 2000);
  };

  const handleCopyAll = (entry) => {
    navigator.clipboard.writeText(entry.instructions || '');
    flashCopied(`${entry.id}-all`);
  };

  const handleCopySelection = (entry) => {
    const selection = window.getSelection ? window.getSelection().toString() : '';
    if (!selection) {
      // Nothing highlighted, tell the user how to use this action.
      flashCopied(`${entry.id}-none`);
      return;
    }
    navigator.clipboard.writeText(selection);
    flashCopied(`${entry.id}-sel`);
  };

  const formatWhen = (iso) => {
    if (!iso) return 'Unknown time';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? iso : d.toLocaleString();
  };

  const previewOf = (text) => {
    const value = text || '';
    if (!value) return '(empty)';
    const firstLine = value.split('\n')[0];
    return firstLine.length > 120 ? `${firstLine.slice(0, 120)}…` : firstLine;
  };

  return (
    <>
      <Tooltip title="Instruction history">
        <IconButton
          onClick={handleOpen}
          size="small"
          className="assistantIconButtonNeutral"
          aria-label="View instruction history"
        >
          <RestoreIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ className: 'assistantDialogPaper' }}
      >
        <DialogTitle className="assistantDialogTitle">
          <Typography variant="h6">
            Instruction History{channelType ? `, ${channelType}` : ''}
          </Typography>
          <IconButton
            onClick={() => setOpen(false)}
            size="small"
            className="assistantIconButtonNeutral"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
              <CircularProgress size={24} />
              <Typography>Loading history…</Typography>
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : history.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No previous versions yet. History is recorded each time these
              instructions are changed.
            </Typography>
          ) : (
            history.map((entry) => {
              const isCopiedAll = copiedKey === `${entry.id}-all`;
              const isCopiedSel = copiedKey === `${entry.id}-sel`;
              const isNoSelection = copiedKey === `${entry.id}-none`;

              return (
                <Accordion
                  key={entry.id}
                  expanded={expandedId === entry.id}
                  onChange={(_e, isExpanded) =>
                    setExpandedId(isExpanded ? entry.id : null)
                  }
                  disableGutters
                  className="assistantSettingsPanel"
                  sx={{ mb: 1 }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary">
                        {formatWhen(entry.created_at)}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {previewOf(entry.instructions)}
                      </Typography>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 1.5,
                        maxHeight: 320,
                        overflow: 'auto',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontFamily: 'monospace',
                        fontSize: '0.85rem',
                        userSelect: 'text',
                        borderRadius: 1,
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'dark' ? '#2d2d2d' : '#f5f5f5',
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      {entry.instructions || '(empty)'}
                    </Box>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 1 }}
                    >
                      Tip: highlight any part of the text above, then use
                      “Copy selection”.
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        mt: 1.5,
                      }}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        className="assistantOutlineButton"
                        startIcon={
                          isCopiedAll ? (
                            <CheckIcon fontSize="small" color="success" />
                          ) : (
                            <ContentCopyIcon fontSize="small" />
                          )
                        }
                        onClick={() => handleCopyAll(entry)}
                      >
                        {isCopiedAll ? 'Copied!' : 'Copy all'}
                      </Button>

                      <Button
                        size="small"
                        variant="outlined"
                        className="assistantOutlineButton"
                        // Prevent the button's mousedown from clearing the
                        // user's current text selection before we read it.
                        onMouseDown={(e) => e.preventDefault()}
                        startIcon={
                          isCopiedSel ? (
                            <CheckIcon fontSize="small" color="success" />
                          ) : (
                            <ContentCopyIcon fontSize="small" />
                          )
                        }
                        onClick={() => handleCopySelection(entry)}
                      >
                        {isCopiedSel
                          ? 'Copied!'
                          : isNoSelection
                          ? 'Select text first'
                          : 'Copy selection'}
                      </Button>

                      <Button
                        size="small"
                        variant="contained"
                        className="assistantPrimaryButton"
                        startIcon={<RestoreIcon fontSize="small" />}
                        onClick={() => handleRestore(entry)}
                      >
                        Restore
                      </Button>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })
          )}
        </DialogContent>

        <DialogActions className="assistantDialogActions">
          <Button onClick={() => setOpen(false)} className="assistantOutlineButton">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InstructionHistoryButton;
